import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { ApplicationDetailResponse, CandidateProfile, MoveApplicationResponse } from '@nest-recruit/shared';
import { ApiError, apiGet } from '../../../lib/api-client';
import { moveApplication, pipelineKey, undoAction } from '../../pipeline/api/pipeline-api';
import { APPLICATIONS_KEY, CANDIDATES_KEY, applicationKey, candidateKey } from './candidates-api';

// Redesign Group 1: the candidate pop-up (CandidatePanel). Its own hooks, beside the profile's.

/**
 * The profile behind the pop-up. Previous / Next swaps the person in place: the person on screen
 * stays until the next one has loaded, never a spinner over what is visible (§5a rule 2).
 */
export function useCandidatePanelProfile(id: string): UseQueryResult<CandidateProfile, ApiError> {
  return useQuery<CandidateProfile, ApiError>({
    queryKey: candidateKey(id),
    queryFn: () => apiGet<CandidateProfile>(`/candidates/${id}`),
    placeholderData: (previous) => previous,
  });
}

/** One application's detail, loaded only when its card is opened ("Match score, answers and history"). */
export function useApplicationOnOpen(id: string, isOpen: boolean): UseQueryResult<ApplicationDetailResponse, ApiError> {
  return useQuery<ApplicationDetailResponse, ApiError>({
    queryKey: applicationKey(id),
    queryFn: () => apiGet<ApplicationDetailResponse>(`/applications/${id}`),
    enabled: isOpen,
  });
}

/** Undo a rejection from its toast (§5a rule 8); the profile and lists refetch afterwards. */
export function useUndoReject() {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, { readonly actionId: string; readonly jobId: string | null }>({
    mutationFn: ({ actionId }) => undoAction(actionId),
    onSettled: async (_data, _error, { jobId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY }),
        queryClient.invalidateQueries({ queryKey: APPLICATIONS_KEY }),
        ...(jobId ? [queryClient.invalidateQueries({ queryKey: pipelineKey(jobId) })] : []),
      ]);
    },
  });
}

export interface RejectVariables {
  readonly candidateId: string;
  readonly applicationId: string;
  readonly jobId: string | null;
  readonly reasonCode: string;
  readonly poolIds: readonly string[];
  readonly newPoolNames: readonly string[];
  readonly addToCvBank: boolean;
}

/**
 * Reject one application from its job card: the pipeline's own move (F8.13), so the reason, the
 * event and the regret letter are the server's as everywhere else. The card reads Rejected at once
 * (§5a rule 1); a refusal puts it back and the caller toasts why.
 */
export function useRejectApplication() {
  const queryClient = useQueryClient();
  return useMutation<MoveApplicationResponse, ApiError, RejectVariables, { readonly previous: CandidateProfile | undefined }>({
    mutationFn: async ({ applicationId, reasonCode, poolIds, newPoolNames, addToCvBank }) => {
      // The profile's rows carry no version; the application's detail does (409 on a stale write).
      const detail = await queryClient.fetchQuery<ApplicationDetailResponse, ApiError>({
        queryKey: applicationKey(applicationId),
        queryFn: () => apiGet<ApplicationDetailResponse>(`/applications/${applicationId}`),
      });
      return moveApplication(
        applicationId,
        { to: 'rejected', reasonCode, poolIds: [...poolIds], newPoolNames: [...newPoolNames], addToCvBank, version: detail.application.version },
        'list',
      );
    },
    onMutate: async ({ candidateId, applicationId }) => {
      await queryClient.cancelQueries({ queryKey: candidateKey(candidateId) });
      const previous = queryClient.getQueryData<CandidateProfile>(candidateKey(candidateId));
      if (previous) {
        queryClient.setQueryData<CandidateProfile>(candidateKey(candidateId), {
          ...previous,
          applications: previous.applications.map((row) => (row.id === applicationId ? { ...row, stage: 'rejected', isLive: false, browseStage: 'rejected' } : row)),
        });
      }
      return { previous };
    },
    onError: (_error, { candidateId }, context) => {
      if (context?.previous) queryClient.setQueryData(candidateKey(candidateId), context.previous);
    },
    // Hold the optimistic state until the refetch lands (§5a rule 3).
    onSettled: async (_data, _error, { jobId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY }),
        queryClient.invalidateQueries({ queryKey: APPLICATIONS_KEY }),
        ...(jobId ? [queryClient.invalidateQueries({ queryKey: pipelineKey(jobId) })] : []),
      ]);
    },
  });
}
